import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import { Summary } from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';

import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import ListColumns from '../../widgets/ListColumns';
import SitePenLogo from '../../widgets/SitepenLogo';

const backEndServicesImg = require('../../assets/images/icon-illustration/back-end-services.svg');
const enterpriseApplicationIntegrationImg = require('../../assets/images/icon-illustration/enterprise-application-integration.svg');
const frontEndToolsImg = require('../../assets/images/icon-illustration/front-end-tools.svg');
const ideaConsultImg = require('../../assets/images/icon-illustration/idea-consult.svg');
const teamEfficiencyImg = require('../../assets/images/illustration/team-efficiency.svg');
const vueDevelopmentImg = require('../../assets/images/illustration/vue-development.svg');
const vueFeatureImg = require('../../assets/images/illustration/vue-feature.svg');
const vueTechnologiesImg = require('../../assets/images/logo-illustration/vue-technologies.svg');
const webAppImg = require('../../assets/images/icon-illustration/web-application.svg');

const factory = create();

export const VueDevelopment = factory(function VueDevelopment() {
	return (
		<div>
			<Head
				title="VueJS Web Development Company | SitePen"
				description="VueJS web development company building enterprise solutions to improve processes and empower business success."
			/>

			<Hero background="level3Green">
				{{
					xlTitle: (
						<div>
							Vue.js
							<br />
							Development
						</div>
					),
					blurb:
						'From greenfield development to minor enhancements, we have the skills and experience to get the most out of Vue.js.'
				}}
			</Hero>

			<Section>
				<ContentWrapper>
					<SummaryList
						style="alternating-reverse"
						listGuttering="none"
						trailingBias="medium"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Advanced Development with Vue.js',
									blurb: (
										<virtual>
											<p>
												Vue.js is one of the primary frameworks that are
												used for creating highly interactive and dynamic web
												applications. Vue.js combines many of the benefits
												of two of the other popular frameworks - Angular and
												React.
											</p>
											<p>
												It is similar to React in that it focuses on the
												application's user interface. The user interface is
												generally one of the most common sources of
												complexity in a web application. Like React, Vue.js
												uses a reactive, component based-approach to allow
												the interface to be broken down into simple, easily
												tested components that can be composed together to
												deliver rich user experiences. A reactive
												architecture enables developers to adopt a
												declarative approach to managing the application
												state - they simply specify the application's
												appearance based on its state, and Vue.js
												efficiently handles the transformations required to
												update the page.
											</p>
											<p>
												Vue.js is similar to Angular in its adoption of the
												classic triad of web technologies- HTML, CSS, and
												JavaScript. This choice allows many development
												teams to quickly add Vue.js into existing
												applications with minimal additional training.
												Vue.js uses intelligent templates bound to custom
												components to generate aspects of the user
												interface, with the reactive bindings being managed
												by the framework itself.
											</p>
										</virtual>
									),
									image: <Image src={vueFeatureImg} size="lg" />
								},
								{
									blurb:
										"SitePen prides itself on being a web-first development company. While we offer an extensive set of development services, front-end development is what we do best. Our extensive experience allows us to meet our clients where they are at. If a client has a particular framework in mind, we can use it to achieve their goals. If, however, they are open to different approaches, then we can analyze their requirements and determine which framework would be best suited to their specific needs. Our ability to help goes beyond merely making suggestions. Our initial engagements with clients often involve an intense analysis of the client's skills, resources, and requirements. These engagements ensure that the solutions we provide are performant, efficient, well-structured, and tailored to fit seamlessly into the client's existing software ecosystem.",
									image: <SitePenLogo />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Vue.js Development Services',
							blurb: (
								<virtual>
									SitePen's rich understanding of Vue.js and the broader web
									application ecosystem makes us an excellent choice for your next
									project.
								</virtual>
							),
							items: [
								{
									title: 'Single-Page App Development',
									blurb:
										"Single-page applications (SPAs) have become the standard for web applications today. Their ability to deliver fluid user experiences while minimizing network traffic makes them an ideal choice for even the most demanding enterprise applications. The Vue.js ecosystem includes a high-quality in-page routing solution and a flux-based statement management solution to support this common type of application. These libraries integrate seamlessly into Vue.js applications to allow engineers to develop SPAs without worrying about conflicts and inconsistent APIs. Vue.js also contains first-class support for animations and transitions. These features are ideally suited for adding subtle visual cues to enhance an application's user experience.",
									image: <Image src={webAppImg} lazy size="icon" align="center" />
								},
								{
									title: 'Custom App Development',
									blurb:
										'Many application development efforts involve adding new features to an existing solution. Vue.js is designed to be very lightweight and easily integrate into existing web applications. Vue.js can manage a small part of the application, allowing new features to be added while leaving the rest of the application unaltered. Since Vue.js only concerns itself with the user interface, it can easily be tied into the existing business and state tracking aspects of the existing application.',
									image: (
										<Image
											src={frontEndToolsImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Vue.js Migration Services',
									blurb:
										'The transition from Vue 2 to Vue 3 is substantial and migrating applications to the latest version of the framework can be challenging. Our team has extensive experience with this kind of migration and has processes in place to make sure that the migration proceeds smoothly. We start by pinning down existing functionality, either by familiarizing ourselves with the existing test suite or developing one that verifies the existing capabilities. We then migrate the project to the latest version of Vue, testing and verifying each step of the process. Finally, when we are sure that everything is working as intended, our engineering team takes advantage of the new features and capabilities to refine and upgrade the application to get the most out of the enhancements that Vue 3 brings.',
									image: (
										<Image
											src={backEndServicesImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Vue.js Consultation',
									blurb:
										'Many of our engagements start as highly technical consultations. Our clients might be considering whether to use Vue.js in a new project, or they might be thinking of enhancing an existing application. They often bring us onto the project to augment their knowledge and experience with our own. These short-term projects allow us to get to know one another and familiarize ourselves with the problem-space. At the end of the engagement, our clients receive a detailed list of recommendations for the next steps that we feel will give the development project its highest chance of success.',
									image: (
										<Image
											src={ideaConsultImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Vue.js Support and Maintenance',
									blurb:
										'Like many web development technologies, Vue.js is constantly evolving and adding new features. We provide support and maintenance services to ensure that existing code bases take advantage of the latest refinements to the framework. These engagements ensure that your applications continue to run efficiently and can grow to meet new user requirements in the future.',
									image: (
										<Image
											src={enterpriseApplicationIntegrationImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<Summary reverse stackedPadding={true}>
						{{
							title: 'Technologies We Use',
							blurb: (
								<virtual>
									<p>
										Vue has a tightly integrated ecosystem. We use each of these
										technologies to maximize the performance and stability of
										our web applications.
									</p>

									<ListColumns>
										<span>Vue.js</span>
										<span>Vue Router</span>
										<span>Vuex</span>
										<span>Vue CLI</span>
										<span>Vue Test Utils</span>
										<span>Jest</span>
										<span>Mocha</span>
										<span>TypeScript</span>
									</ListColumns>
								</virtual>
							),
							image: <Image src={vueTechnologiesImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<IndustryExpertise />
			</Section>
			<Section>
				<ContentWrapper>
					<SummaryList
						style="alternating"
						listGuttering="none"
						trailingBias="medium"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Choosing the Right Vue.js Development Team',
									blurb:
										"Vue.js makes it much easier to create high-performance web applications than many other frameworks. However, Vue offers a lot of flexibility with how the application is organized. To get the most out of Vue.js, then, a development team needs to have the architectural knowledge to develop the ideal layout for the application's business logic, state management, and server integration subsystems to optimize development velocity, application performance, and maintainability. Ultimately, Vue.js is a tool for building web applications. When used by a skilled and experienced team, it can be used to create cutting-edge applications that offer exceptional user experiences. Without the proper skill set, however, a development team can fail to meet the project's goals regardless of their tools' quality.",
									image: <Image src={teamEfficiencyImg} lazy size="lg" />
								},
								{
									title: 'Benefits of Using Vue.js',
									blurb:
										"Vue.js is an excellent choice for building small- to medium-sized enterprise applications. Its tightly integrated ecosystem delivers a consistent developer experience no matter how little or how much of Vue's ecosystem is being used. Additionally, Vue.js uses HTML, CSS, and JavaScript, the three elementary front-end technologies; developers don't need to learn new languages to start using it. Finally, since Vue.js doesn't mandate how the business logic and application state are managed, it is an excellent framework for teams that want to maximize their freedom to organize the application's non-visual aspects.",
									image: <Image src={vueDevelopmentImg} lazy size="md" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="Vue.js FAQs">
					{[
						{
							title: 'What Can Vue.js Do?',
							content: (
								<p>
									Vue.js is a full-featured web application development framework
									that is optimized for delivering high-performance single-page
									applications. Its robust component-based reactive architecture
									encourages developers to follow best practices, such as code
									reuse, through mechanisms such as custom directives and mixins.
								</p>
							)
						},
						{
							title: 'Why Should My Business Use Vue.js?',
							content: (
								<p>
									Vue.js may be a good solution to explore if your development
									teams have a lot of experience with HTML, CSS, and
									JavaScript-based applications and are comfortable continuing
									with those technologies as the basis for their applications.
									Vue.js is also an excellent choice for adding new features to
									existing applications using a cutting-edge framework. Vue.js is
									very lightweight and is designed to integrate with existing
									applications seamlessly.
								</p>
							)
						},
						{
							title: 'What Are Some Features of the Vue.js Framework?',
							content: (
								<p>
									Vue.js is a component-based web application framework that uses
									a reactive architecture to synchronize the user interface to its
									state. Its component system contains powerful features such as
									web component-inspired slots, dependency injection, asynchronous
									components, mixins, and more. This rich set of capabilities
									allows developers to optimize each component to perform well
									while remaining open to reuse and future enhancements. Beyond
									the core offering, Vue.js contains additional libraries that
									support in-page routing for single-page web applications and
									flux-based state management via the Vuex project. Finally, to
									give developers the best possible experience, Vue.js has
									additional projects that simplify test creation and enhance the
									native debugging capabilities of web browsers with Vue-specific
									tools.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default VueDevelopment;
